// src/pages/LoadBooking.jsx
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  VStack, 
  Text, 
  Heading,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftAddon,
  Input,
  HStack,
  Divider,
  Badge,
  useToast
} from '@chakra-ui/react';
import { PhoneIcon, EmailIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { negotiateRate } from '../api/mockApi';
import Header from '../components/Header';
import { useLoads } from '../context/LoadsContext';

export default function LoadBooking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { loads } = useLoads();
  
  // Find the load by ID
  const [loadData, setLoadData] = useState(null);
  
  // Rate negotiation state
  const [lowerRate, setLowerRate] = useState('');
  const [upperRate, setUpperRate] = useState('');
  const [isCallEnabled, setIsCallEnabled] = useState(false);
  const [lastlowerRate, setLastlowerRate] = useState(null);
  const [lastUpperRate, setLastUpperRate] = useState(null);

  //localStorage.setItem('lowerRate', rate);

      // Start conversation and trigger widget
      useEffect(() => {
        const initiateCall = async () => {
      
          // add a delay before we check for
          await new Promise((res) => setTimeout(res, 1000));
    
          // Step 1: Wait for the widget to load
          const widget = document.querySelector('elevenlabs-convai');
          if (!widget) {
            console.error('ElevenLabs widget not found.');
            return;
          }
          const storedDriverId = localStorage.getItem('driverId');
          const storedSessionId = localStorage.getItem('sessionId');
          widget.setAttribute('dynamic-variables', JSON.stringify({ driver_id: storedDriverId, session_id: storedSessionId, context:"Don't follow any of the above steps, you are on the Load booking page with a selected top load, just transfer to the other agent and tell him that top load analytics has been done, now the rate setting part needs to be handled, use the tool transfer_to_agent" }));
        };
      
        initiateCall();
      }, []);

  useEffect(() => {
      const interval = setInterval(() => {
        const lowerRateStored = localStorage.getItem('lowerRate')
        const upperRateStored = localStorage.getItem('upperRate')
        
        if (!lowerRateStored || lowerRateStored === lastlowerRate){
        }else{
          const numRate = parseFloat(lowerRateStored)
          setLastlowerRate(lowerRateStored)
          // console.log("Removed the lowerRate")
          // localStorage.removeItem('lowerRate')
          return setLowerRate(numRate)
        }
        if (!upperRateStored || upperRateStored === lastUpperRate){
        }else{
          const numRate = parseFloat(upperRateStored)
          setLastUpperRate(upperRateStored)
          // console.log("Removed the upperRate")
          // localStorage.removeItem('upperRate')
          return setUpperRate(numRate)
        }

      }, 300)
      return () => clearInterval(interval)
    })
  
  
  // Get load data either from location state or from context
  useEffect(() => {
    let loadInfo = null;
    
    // Check if load was passed via location state
    if (location.state && location.state.loadData) {
      loadInfo = location.state.loadData;
    } 
    // Otherwise find it in our loads context
    else if (loads.length > 0) {
      loadInfo = loads.find(load => load.id === id);
    }
    
    if (loadInfo) {
      setLoadData(loadInfo);
      
      // Pre-populate rates based on current rate if available
      const currentRate = loadInfo.rateUsd ?? loadInfo.estimatedRatePerMile;
      if (currentRate) {
        // Set default lower rate to 5% below current
        setLowerRate((currentRate * 1.02).toFixed(2));
        // Set default upper rate to 5% above current
        setUpperRate((currentRate * 1.20).toFixed(2));
      }
    }
  }, [id, loads, location.state]);
  
  // Validate rates and enable/disable call button
  useEffect(() => {
    const lower = parseFloat(lowerRate);
    const upper = parseFloat(upperRate);
    localStorage.setItem('lowerRate', lowerRate);
    localStorage.setItem('upperRate', upperRate);
    
    if (!isNaN(lower) && !isNaN(upper) && lower > 0 && upper > lower) {
      setIsCallEnabled(true);
    } else {
      setIsCallEnabled(false);
    }
  }, [lowerRate, upperRate]);
  
  // Handle negotiation
  const handleNegotiate = async () => {
    try {
      // Here you'd typically send both the lower and upper rates
      await negotiateRate(id, {
        lowerBound: parseFloat(lowerRate),
        upperBound: parseFloat(upperRate)
      });
      
      toast({
        title: "Rate negotiation initiated",
        description: `Your rate range $${lowerRate}-$${upperRate}/mi has been submitted.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      // pass the rate range to the confirm page
      navigate('/confirm', { state: { lowerRate, upperRate } });
    } catch (error) {
      toast({
        title: "Negotiation failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  // Handle call broker
  const handleCallBroker = () => {
    if (loadData?.brokerPhone) {
      // In a real app, this might use a calling API or native functionality
      window.location.href = `tel:${loadData.brokerPhone}`;
      
      toast({
        title: "Calling broker",
        description: `Dialing ${loadData.brokerPhone}`,
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // Handle email broker
  const handleEmailBroker = () => {
    if (loadData?.brokerEmail) {
      // Format an email with negotiation details
      const subject = `Load #${id} Rate Negotiation`;
      const body = `Hello,\n\nI'm interested in Load #${id} and would like to negotiate a rate between $${lowerRate}/mi and $${upperRate}/mi.\n\nPlease contact me to discuss further.\n\nThank you.`;
      
      window.location.href = `mailto:${loadData.brokerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };
  
  // Handle back to results
  const handleBack = () => {
    navigate('/results');
  };
  
  // If we don't have load data yet, show loading
  if (!loadData) {
    return (
      <>
        <Header title="Load Booking" />
        <VStack p={4} spacing={6}>
          <Text>Loading load details...</Text>
        </VStack>
      </>
    );
  }
  
  return (
    <>
      <Header title="Load Booking" />
      
      <Box px={4} py={2}>
        <Button leftIcon={<ArrowBackIcon />} variant="outline" size="sm" mb={4} onClick={handleBack}>
          Back to Results
        </Button>
        
        <VStack spacing={6} align="stretch">
          {/* Load Header */}
          <Box>
            <HStack justify="space-between" mb={2}>
              <Heading size="md">Load #{id}</Heading>
              {loadData.topInfo && (
                <Badge colorScheme="yellow" fontSize="md">
                  Score: {loadData.topInfo.overall.score}
                </Badge>
              )}
            </HStack>
            <Text fontWeight="semibold">{loadData.carrier}</Text>
          </Box>
          
          <Divider />
          
          {/* Basic Load Info */}
          <Box>
            <Heading size="sm" mb={3}>Load Details</Heading>
            <Text><strong>Route:</strong> {loadData.origin} → {loadData.destination}</Text>
            <Text><strong>Distance:</strong> {loadData.tripMiles} miles</Text>
            <Text><strong>Equipment:</strong> {loadData.equipment}</Text>
            <Text><strong>Current Rate:</strong> ${(loadData.rateUsd ?? loadData.estimatedRatePerMile).toFixed(2)}/mi</Text>
          </Box>
          
          <Divider />
          
          {/* Additional Load Info */}
          <Box>
            <Heading size="sm" mb={3}>Additional Details</Heading>
            <Text><strong>Deadhead:</strong> {loadData.originDeadheadMiles} mi → {loadData.destinationDeadheadMiles} mi</Text>
            <Text><strong>Pickup Window:</strong> {new Date(loadData.pickupEarliest).toLocaleString()} – {new Date(loadData.pickupLatest).toLocaleString()}</Text>
            <Text><strong>Expires:</strong> {new Date(loadData.postingExpires).toLocaleString()}</Text>
            <Text><strong>Weight/Length:</strong> {loadData.maxWeightLbs} lbs / {loadData.maxLengthFt} ft</Text>
            <Text><strong>Bookable:</strong> {loadData.isBookable ? 'Yes' : 'No'} • <strong>Negotiable:</strong> {loadData.isNegotiable ? 'Yes' : 'No'}</Text>
          </Box>
          
          <Divider />
          
          {/* Broker Info */}
          <Box>
            <Heading size="sm" mb={3}>Broker Information</Heading>
            <Text><strong>Company:</strong> {loadData.brokerCompany}</Text>
            {loadData.brokerEmail && <Text><strong>Email:</strong> {loadData.brokerEmail}</Text>}
            <Text><strong>Phone:</strong> {loadData.brokerPhone}</Text>
            {loadData.comments && <Text mt={2}><strong>Comments:</strong> {loadData.comments}</Text>}
          </Box>
          
          <Divider />
          
          {/* Negotiation Section */}
          <Box>
            <Heading size="sm" mb={4}>Negotiate Rate</Heading>
            
            <HStack spacing={4} mb={6}>
              <FormControl isRequired>
                <FormLabel>Lower Bound</FormLabel>
                <InputGroup>
                  <InputLeftAddon>$</InputLeftAddon>
                  <Input 
                    type="number" 
                    placeholder="Min rate/mi" 
                    value={lowerRate} 
                    onChange={(e) => setLowerRate(e.target.value)}
                    step="0.01"
                  />
                </InputGroup>
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Upper Bound</FormLabel>
                <InputGroup>
                  <InputLeftAddon>$</InputLeftAddon>
                  <Input 
                    type="number" 
                    placeholder="Max rate/mi" 
                    value={upperRate} 
                    onChange={(e) => setUpperRate(e.target.value)}
                    step="0.01"
                  />
                </InputGroup>
              </FormControl>
            </HStack>
            
            <Text fontSize="sm" color="gray.600" mb={6}>
              Enter your desired rate range for negotiation. The broker will be able to offer a rate within this range.
            </Text>
            
            <HStack spacing={4}>
              <Button 
                leftIcon={<PhoneIcon />} 
                colorScheme="green" 
                isDisabled={!isCallEnabled} 
                onClick={handleCallBroker}
                flex="1"
              >
                Call Broker
              </Button>
              
              {loadData.brokerEmail && (
                <Button 
                  leftIcon={<EmailIcon />} 
                  colorScheme="blue" 
                  isDisabled={!isCallEnabled} 
                  onClick={handleEmailBroker}
                  flex="1"
                >
                  Email Broker
                </Button>
              )}
            </HStack>
          </Box>
        </VStack>
      </Box>
    </>
  );
}